import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useNavigate } from "react-router-dom";

const InteractiveSpeciesCounter = () => {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({
    birds: 165,
    mammals: 30,
    reptiles: 19,
    plants: 800
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setCounts((prev) => ({
        birds: prev?.birds + (Math.random() > 0.8 ? 1 : 0),
        mammals: prev?.mammals + (Math.random() > 0.95 ? 1 : 0),
        reptiles: prev?.reptiles + (Math.random() > 0.97 ? 1 : 0),
        plants: prev?.plants + (Math.random() > 0.7 ? 1 : 0)
      }));
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const speciesData = [
    {
      id: 1,
      icon: "Bird",
      name: "Bird Species",
      count: counts?.birds,
      description: "Including 15 endemic species found nowhere else on Earth",
      color: "var(--color-accent)",
      bgColor: "var(--color-accent)/10",
      featured: "Philippine Cockatoo"
    },
    {
      id: 2,
      icon: "Squirrel",
      name: "Mammal Species",
      count: counts?.mammals,
      description: "From tiny bats to the endangered Palawan Bearcat",
      color: "var(--color-secondary)",
      bgColor: "var(--color-secondary)/10",
      featured: "Palawan Bearcat"
    },
    {
      id: 3,
      icon: "Bug",
      name: "Reptile Species",
      count: counts?.reptiles,
      description: "Unique lizards and snakes adapted to cave ecosystems",
      color: "var(--color-primary)",
      bgColor: "var(--color-primary)/10",
      featured: "Monitor Lizard"
    },
    {
      id: 4,
      icon: "Leaf",
      name: "Plant Species",
      count: counts?.plants,
      description: "Ancient forest giants and rare medicinal plants",
      color: "var(--color-success)",
      bgColor: "var(--color-success)/10",
      featured: "Almaciga Tree"
    }
  ];

  return (
    <section className="py-20 bg-[var(--color-background)]">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-[var(--color-success)]/10 rounded-full px-4 py-2 mb-4">
            <Icon name="Sparkles" size={20} color="var(--color-success)" />
            <span className="text-[var(--color-success)] text-sm font-medium">
              Living Biodiversity Database
            </span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-[var(--color-foreground)] mb-4">
            A Sanctuary for Life
          </h2>
          <p className="text-lg text-[var(--color-muted-foreground)] max-w-2xl mx-auto">
            Real-time monitoring of species diversity within the park's protected boundaries. Numbers update as researchers document new discoveries.
          </p>
        </div>

        {/* Species Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {speciesData?.map((species) => (
            <div
              key={species?.id}
              className="bg-[var(--color-card)] rounded-xl p-6 border border-[var(--color-border)] hover:shadow-conservation-lg transition-all animation-organic group"
            >
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                style={{ backgroundColor: species?.bgColor }}
              >
                <Icon name={species?.icon} size={32} color={species?.color} />
              </div>
              <div
                className="text-5xl font-bold mb-2 pulse-conservation"
                style={{ color: species?.color }}
              >
                {species?.count}
              </div>
              <div className="text-xl font-semibold text-[var(--color-foreground)] mb-2">
                {species?.name}
              </div>
              <p className="text-sm text-[var(--color-muted-foreground)] mb-3">
                {species?.description}
              </p>
              <div className="flex items-center space-x-2 text-xs text-[var(--color-muted-foreground)]">
                <Icon name="Star" size={14} color={species?.color} />
                <span>Featured: {species?.featured}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Total Count Banner */}
        <div className="bg-gradient-conservation rounded-2xl p-8 text-center shadow-conservation-lg">
          <div className="max-w-3xl mx-auto">
            <div className="text-6xl lg:text-7xl font-bold text-[var(--color-accent)] mb-4">
              {counts?.birds + counts?.mammals + counts?.reptiles + counts?.plants}+
            </div>
            <div className="text-2xl lg:text-3xl font-semibold text-[var(--color-foreground)] mb-4">
              Total Species Under Protection
            </div>
            <p className="text-lg text-[var(--color-muted-foreground)] mb-6">
              Every species plays a critical role in maintaining the delicate balance of this UNESCO World Heritage ecosystem
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                iconName="Database"
                iconPosition="left"
                onClick={() => navigate("/biodiversity-status-center")}
              >
                Explore Full Database
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="Download"
                iconPosition="left"
                onClick={() => navigate("/research-data-portal")}
              >
                Download Research Data
              </Button>
            </div>
          </div>
        </div>

        {/* Live Update Indicator */}
        <div className="text-center mt-8">
          <div className="inline-flex items-center space-x-2 text-sm text-[var(--color-muted-foreground)]">
            <div className="w-2 h-2 bg-[var(--color-success)] rounded-full pulse-conservation"></div>
            <span>Live data • Updated every 3 seconds • Last sync: {new Date()?.toLocaleTimeString()}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default InteractiveSpeciesCounter;